import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ThumbnailApiClient } from '../../api/thumbnailApiClient';
import { folderGreySvg, thumbnailFallbackSvg } from '../../assets/images';
import { StateShape as ReduxStateShape } from '../../reducers';
import { SpinnerIcon } from '../BaseComponents';

interface ThumbnailProps {
    resourceId: string | null;
    size: string | null;
    isPublicRefComponent?: boolean;
    isFolder?: boolean;
}

export const thumbnailApiCllient = new ThumbnailApiClient();

const Thumbnail = ({ resourceId, size, isPublicRefComponent, isFolder }: ThumbnailProps) => {
    // loading for image fetching
    const [loading, setLoading] = useState<boolean>(true);
    const [img, setImg] = useState<string>();

    const { accessToken } = useSelector((state: ReduxStateShape) => ({
        accessToken: state.auth.user?.access_token,
    }));


    // check the format for the size to be like '100px-100px'
    const checkSizeFormat = (size: string) => {
        return /^\d+x\d+$/.test(size);
    };

    // Memorize the result of the API call based on resourceId, size, and accessToken
    const memorizedImg = useMemo(() => {
        // Don't call API for folders, just return null to show folder icon
        if (isFolder) {
            return null;
        }

        if (accessToken && resourceId && size && checkSizeFormat(size)) {
            return thumbnailApiCllient.getThumbnail(
                resourceId,
                size,
                {
                    accessToken,
                },
                isPublicRefComponent
            );
        }
        return null;
    }, [accessToken, resourceId, size, isPublicRefComponent, isFolder]);

    useEffect(() => {
        if (isFolder) {
            setImg(folderGreySvg);
            setLoading(false);
            return;
        }

        // Try API thumbnail first
        if (memorizedImg) {
            setLoading(true);
            memorizedImg
                .then((imageUrl) => {
                    if (imageUrl) {
                        setImg(imageUrl);
                    } else {
                        // API failed, use fallback icon
                        setImg(thumbnailFallbackSvg);
                    }
                })
                .catch((error) => {
                    console.error('API thumbnail failed:', error);
                    // API failed, use fallback icon
                    setImg(thumbnailFallbackSvg);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            // Use fallback icon
            setImg(thumbnailFallbackSvg);
            setLoading(false);
        }
    }, [isFolder, memorizedImg]);

    return (
        <div className="thumbnail">
            {loading ? (
                <div className="loading" data-testid="loading-spinner">
                    <SpinnerIcon />
                </div>
            ) : (
                <img
                    className={`thumbnail-image ${
                        img === '/static/media/thumbnailFallback.31de7cd4.svg'
                            ? 'placeholder-thumbnail'
                            : img?.startsWith('data:')
                              ? 'custom-thumbnail'
                              : ''
                    }`}
                    src={img}
                    alt="thumbnail"
                />
            )}
        </div>
    );
};

export default Thumbnail;
